import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Skeleton
} from '@mui/material';
import { 
  Add as AddIcon,
  Flag as FlagIcon,
  Place as PlaceIcon,
  SportsScore as SportsScoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { racetrack as racetrackApi } from '../../utils/api';
import { useAuth } from '../../components/auth/AuthModule';
import CreateRacetrackModal from './components/CreateRacetrackModal';
import { COUNTRIES } from '../../constants/countries';
import { COUNTRY_TO_CODE } from '../../utils/countryUtils';
import ReactCountryFlag from 'react-country-flag';
import ImportRacetrackModal from './components/ImportRacetrackModal';

// Import the countries translations
import countriesEn from '../../constants/countries/en.json';
import countriesEs from '../../constants/countries/es.json';
import countriesCa from '../../constants/countries/ca.json';

const countryTranslations = {
  en: countriesEn,
  es: countriesEs,
  ca: countriesCa
};

const RacetracksPage = () => {
  const { t, i18n } = useTranslation(['racetracks', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [racetracks, setRacetracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);

  useEffect(() => {
    fetchRacetracks();
  }, []);

  const fetchRacetracks = async () => {
    try {
      setLoading(true);
      const data = await racetrackApi.getAll();
      setRacetracks(data);
      
      // Extract unique countries for filtering
      const uniqueCountries = [...new Set(data.map(track => track.country))];
      setAvailableCountries(uniqueCountries);
    } catch (error) {
      console.error('Error fetching racetracks:', error);
      toast.error(t('errors.fetchingRacetracks'));
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleViewRacetrack = (id) => {
    navigate(`/racetracks/${id}`);
  };

  const handleCreateSuccess = () => {
    setCreateModalOpen(false);
    fetchRacetracks();
    toast.success(t('success.racetrackCreated'));
  };

  const getFilteredRacetracks = () => {
    if (selectedCountry === 'all') {
      return racetracks;
    }
    return racetracks.filter(track => track.country === selectedCountry);
  };

  // Get translated country name
  const getCountryName = (countryCode) => {
    const translations = countryTranslations[i18n.language] || countryTranslations.en;
    return translations[countryCode] || countryCode;
  };

  const renderRacetrackCard = (racetrack) => {
    const countryCode = COUNTRY_TO_CODE[racetrack.country];
    
    return (
      <Grid item xs={12} sm={6} md={4} key={racetrack._id}>
        <Card 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: (theme) => theme.shadows[4]
            }
          }}
          onClick={() => handleViewRacetrack(racetrack._id)}
        >
          <CardContent sx={{ flexGrow: 1, p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              {countryCode ? (
                <Avatar 
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    borderColor: 'divider',
                    p: 0,
                    overflow: 'hidden'
                  }}
                >
                  <ReactCountryFlag 
                    countryCode={countryCode} 
                    svg 
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    title={getCountryName(racetrack.country)}
                  />
                </Avatar>
              ) : (
                <Avatar 
                  sx={{ 
                    width: 56, 
                    height: 56,
                    bgcolor: 'action.selected'
                  }}
                >
                  <FlagIcon fontSize="large" />
                </Avatar>
              )}
              <Box ml={2}>
                <Typography variant="h6" component="h2" noWrap>
                  {racetrack.name}
                </Typography>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between'
              }}
            >
              <Box display="flex" alignItems="center">
                <PlaceIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {racetrack.location}
                </Typography>
              </Box>
              <Chip 
                label={`${racetrack.length.toFixed(1)} ${t('racetrackList.kilometers')}`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderNoRacetracks = () => (
    <Box textAlign="center" py={5}>
      <Paper sx={{ p: 3, maxWidth: 500, mx: 'auto' }}>
        <SportsScoreIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t('racetracks.noRacetracks')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('racetracks.addFirstRacetrack')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          {t('racetracks.addRacetrack')}
        </Button>
      </Paper>
    </Box>
  );

  const renderSkeletonCard = () => (
    <Grid item xs={12} sm={6} md={4}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Skeleton 
              variant="circular" 
              width={56} 
              height={56} 
            />
            <Box ml={2} flex={1}>
              <Skeleton variant="text" width="80%" height={32} />
            </Box>
          </Box>
          
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between'
            }}
          >
            <Box display="flex" alignItems="center" flex={1}>
              <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
              <Skeleton variant="text" width="60%" height={24} />
            </Box>
            <Skeleton variant="rounded" width={100} height={32} />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box>
              <Skeleton variant="text" width={300} height={48} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width={200} height={56} />
            </Box>
            <Box>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1, display: 'inline-block' }} />
              <Skeleton variant="rounded" width={180} height={40} sx={{ display: 'inline-block' }} />
            </Box>
          </Box>

          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <React.Fragment key={item}>
                {renderSkeletonCard()}
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  const filteredRacetracks = getFilteredRacetracks();

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          mb={4}
          gap={2}
        >
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 2, sm: 1 }}
            width={{ xs: '100%', sm: 'auto' }}
          >
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setImportModalOpen(true)}
              sx={{ 
                mr: { sm: 1 },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {t('racetracks.import')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
              sx={{ 
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {t('racetracks.addRacetrack')}
            </Button>
          </Box>
          <Box width={{ xs: '100%', sm: 'auto' }}>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>{t('racetrackList.country')}</InputLabel>
              <Select
                value={selectedCountry}
                onChange={handleCountryChange}
                label={t('racetrackList.country')}
                fullWidth
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {selected !== 'all' && COUNTRY_TO_CODE[selected] && (
                      <ReactCountryFlag 
                        countryCode={COUNTRY_TO_CODE[selected]} 
                        svg 
                        style={{ 
                          width: '1.5em', 
                          height: '1.5em',
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}
                      />
                    )}
                    {selected === 'all' ? t('common:common.all') : getCountryName(selected)}
                  </Box>
                )}
              >
                <MenuItem value="all">{t('common:common.all')}</MenuItem>
                {COUNTRIES.filter(country => availableCountries.includes(country)).map(country => (
                  <MenuItem key={country} value={country}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {COUNTRY_TO_CODE[country] && (
                        <ReactCountryFlag 
                          countryCode={COUNTRY_TO_CODE[country]} 
                          svg 
                          style={{ 
                            width: '1.5em', 
                            height: '1.5em',
                            marginRight: '8px',
                            borderRadius: '2px'
                          }}
                        />
                      )}
                      {getCountryName(country)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {filteredRacetracks.map(renderRacetrackCard)}
        </Grid>
      </Box>

      <CreateRacetrackModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <ImportRacetrackModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={() => {
          setImportModalOpen(false);
          fetchRacetracks();
        }}
      />
    </Container>
  );
};

export default RacetracksPage; 